import { FeatureFlagKey, type FeatureFlagKeyType } from '../types/feature-flags'

/**
 * Maps a Help Center category (help_entry.module_slug) to the feature-flag KEY
 * that gates its availability. `null` means the category/article is always
 * visible (general guidance: getting started, account/admin basics, settings,
 * integrations, communications).
 *
 * Gating help by feature flag — not by isPersonal — means a personal account
 * (business flags OFF) never sees help for Tickets, Invoices, Time Tracking,
 * Deals, Inventory, Forms, Team, etc., AND any business org that disables a
 * module automatically loses its help too. This mirrors MODULE_TYPE_FEATURE_FLAG
 * (which hides permission categories the same way).
 *
 * The authoritative per-entry value lives in help_entry.feature_flag_key (set by
 * the seed / admin). This map is the fallback used to derive a category's flag
 * from its slug (e.g. for the category list, which only carries slug + count),
 * and to default admin-created entries.
 */
export const HELP_MODULE_FEATURE_FLAG: Record<string, string | null> = {
  // Always-visible / general
  'getting-started': null,
  admin: null,
  settings: null,
  integrations: null,
  communications: null,

  // Core personal + business modules (on in personal accounts)
  documents: FeatureFlagKey.DOCUMENTS.key,
  notes: FeatureFlagKey.NOTES.key,
  projects: FeatureFlagKey.PROJECTS.key,
  tasks: FeatureFlagKey.TASKS.key,
  contacts: FeatureFlagKey.CONTACTS.key,
  clients: FeatureFlagKey.ENTITIES.key, // "Clients" is the Entities/People module

  // Business modules (off in the personal profile)
  crm: FeatureFlagKey.DEALS.key,
  contracts: FeatureFlagKey.CONTRACTS.key,
  inventory: FeatureFlagKey.INVENTORY.key,
  invoices: FeatureFlagKey.INVOICES.key,
  team: FeatureFlagKey.TEAM.key,
  tickets: FeatureFlagKey.TICKETS.key,
  time: FeatureFlagKey.TIME_TRACKING.key,
  forms: FeatureFlagKey.FORMS.key,
}

/**
 * Derive the gating feature-flag KEY for a help category slug. Returns `null`
 * for general categories (and unknown slugs — fail open to "always visible" so a
 * new, unmapped category is never hidden by accident).
 */
export function helpFeatureFlagForModule(
  moduleSlug: string | null | undefined,
): string | null {
  if (!moduleSlug) return null
  return HELP_MODULE_FEATURE_FLAG[moduleSlug] ?? null
}

/**
 * Resolve a feature-flag KEY string (e.g. "tickets", "time_tracking") back to
 * its FeatureFlagKeyType (e.g. "TICKETS", "TIME_TRACKING") so callers can look
 * it up in the flag map returned by useFeatureFlags. Returns `undefined` for an
 * unknown key.
 */
export function featureFlagKeyTypeFromKey(
  key: string | null | undefined,
): FeatureFlagKeyType | undefined {
  if (!key) return undefined
  return (Object.keys(FeatureFlagKey) as FeatureFlagKeyType[]).find(
    type => FeatureFlagKey[type].key === key,
  )
}
